import { useCallback, useMemo, useState } from 'react';
import { useGqlMutation } from '@API/useAvQuery';
import { ErrorTypes, FeatureFlags, QueryOrigin } from '@fabric-ui/components';
import { noop } from '@fabric-ui/utils';
import { QueryObserverOptions, QueryObserverResult, useQueries, useQuery } from '@tanstack/react-query';
import { avRum } from '@utils/AvRum';
import { gql } from 'graphql-request';
import { useAvContext } from '../context/AvContextProvider';
import { generateUUID, isNullOrUndefined } from '../utils/utils';
import { QueryResponse } from '../views/CustomDashboards/types';
import { useDebounceV1 } from './useDebounce';
import { UseQuerySqlProps } from './useQuerySql.types';

/** AVA-13672 */
const registerAbortSignalLog = (signal?: AbortSignal, req?: unknown) => {
  const handleAbort = (abortSignalEvent: Event) => {
    avRum.addAction('Graphql:querySql:RequestAbort', { abortSignalEvent, req });
  };
  signal?.addEventListener('abort', handleAbort);
  return () => {
    signal?.removeEventListener('abort', handleAbort);
  };
};

export const QUERY_SQL_FRAGMENT = `
  fragment QuerySqlFields on DwQueryResponse {
    totalRowCount
    rows {
      data
      id
    }
    queryId
  }
`;

export const QUERY = gql`
  query querySql($req: Json!) {
    querySql(value: $req) {
      ...QuerySqlFields
    }
  }

  ${QUERY_SQL_FRAGMENT}
`;

export const CANCEL_QUERY = gql`
  mutation cancelQuery($queryId: String!) {
    cancelQuery(queryId: $queryId)
  }
`;

export const CANCEL_ASYNC_QUERY = gql`
  mutation cancelAsyncQuery($queryId: String!) {
    cancelAsyncQuery(queryId: $queryId)
  }
`;

type QuerySqlResponse = {
  data: any;
  totals?: number;
  isLoading: boolean;
  errors?: any[];
  refetch: () => Promise<any>;
  isRefetching: boolean;
  isPreviousData: boolean;
};

/**
 * @deprecated - use typed useQueryObject instead
 */
export default function useQuerySql({
  key,
  sql,
  origin = QueryOrigin.INTERACTIVE_QUERY,
  usage,
  searchQuery = '',
  onSuccess = d => d,
  onError = undefined,
  options = {},
  muteErrors = undefined,
  additionalFilter = undefined,
  debounceQueries = true,
  showTotals = false,
  gql,
  tags,
}: UseQuerySqlProps): QuerySqlResponse {
  const dummyUUID: string = useMemo(generateUUID, []);
  const { api, featureFlags, accountId } = useAvContext();
  const sqls = Array.isArray(sql) ? sql : [sql];
  const [, setPrevData] = useState(false);
  const keys = useMemo(() => (Array.isArray(sql) ? sql.map(sql => [key, sql]) : [[key, sql]]), [sql, key]);
  const debouncedKeys = useDebounceV1(keys, debounceQueries ? 500 : 0);
  const uuids = useMemo(() => debouncedKeys.map(() => `${generateUUID()}_${accountId}`), [debouncedKeys]);
  const { mutate: cancel } = useGqlMutation({ mutation: CANCEL_QUERY, onError: noop });
  const queries =
    !sql || options.enabled === false || !sql[0]
      ? [{ queryKey: [dummyUUID] } as QueryObserverOptions]
      : sqls.map((SQL, index) => ({
          queryKey: debouncedKeys[index],
          queryFn: ({ signal }: { signal?: AbortSignal }): QueryResponse => {
            const req = {
              sql: SQL,
              ...(origin && { origin }),
              ...(searchQuery && { searchQuery }),
              totals: { totalRowCount: showTotals },
              ...(usage && { pathAliasNamespace: usage }),
              additionalFilter,
              queryId: uuids[index],
              ...(tags && { tags }),
            };

            const cleanup = registerAbortSignalLog(signal, req);
            return api(gql || QUERY, {
              options: { req },
              onError,
              signal,
              muteErrors,
              onSuccess: data => {
                const flattenedData = data?.data?.querySql?.rows?.map(row => ({ ...row.data, id: row.id }));
                if (data === undefined || data.errors) {
                  throw data?.errors || new Error(`${ErrorTypes.Network}: ${keys[index]}`);
                }
                setPrevData(true);
                return {
                  data: flattenedData?.length ? onSuccess(flattenedData, index) : flattenedData,
                  totals: data?.data?.querySql?.totalRowCount,
                };
              },
            })
              .catch(e => {
                if (signal?.aborted && uuids[index]) {
                  cancel({ _accountId: uuids[index].split('_')[1], queryId: uuids[index] });
                }
                if (muteErrors && !signal?.aborted) {
                  onError?.(e);
                } else {
                  throw e;
                }
              })
              .finally(cleanup);
          },
          enabled: !!SQL && !!sql,
          gcTime: featureFlags[FeatureFlags.disableCacheQueryClient] ? 0 : undefined,
          ...options,
        }));

  const queryEnabled = queries.every(r => isNullOrUndefined(r.enabled) || r.enabled);

  const resultsMulti: QueryObserverResult<{ data: any; totals: number }>[] = useQueries({
    queries: queries.map(m => ({ ...m, enabled: queryEnabled && queries.length > 1 })),
  });

  const resultSingle: QueryResponse & QueryObserverResult = useQuery({
    ...queries[0],
    enabled: queryEnabled && queries.filter(f => !!f.queryFn).length === 1,
  });

  const refetchMulti = useCallback(() => Promise.all(resultsMulti.map(r => r.refetch())), [resultsMulti]);
  const result = useMemo(
    () =>
      queries.length > 1
        ? {
            totals: !resultsMulti.length ? undefined : resultsMulti[0].data?.totals,
            data:
              !resultsMulti.length || (queries.length > 1 && resultsMulti.some(({ data }) => !data?.data?.length))
                ? undefined
                : resultsMulti.length === 1
                  ? resultsMulti[0].data?.data
                  : resultsMulti.map(({ data }) => data?.data),
            isLoading: resultsMulti.some(result => result.isLoading),
            errors: resultsMulti.map(result => result.error).filter(e => !!e) || [],
            refetch: refetchMulti,
            isRefetching: resultsMulti.some(result => result.isFetching) || (debounceQueries && debouncedKeys !== keys),
            isPreviousData: resultsMulti.some(result => result.isPlaceholderData),
          }
        : {
            data: resultSingle.data?.data,
            totals: resultSingle.data?.totals,
            isLoading: resultSingle.isLoading,
            errors: resultSingle.error ? [resultSingle.error] : [],
            refetch: resultSingle.refetch,
            isRefetching: resultSingle.isRefetching || (debounceQueries && debouncedKeys !== keys),
            isPreviousData: resultSingle.isPlaceholderData,
          },
    [resultsMulti, resultSingle, debouncedKeys]
  );

  return result;
}

export const queryOrderBy = (map: any[], filterArr?: any[], isTableV2?: boolean) => {
  const orderByFields = map
    .filter(({ property }) => !filterArr || filterArr.find(({ id }) => id === property))
    .map(({ property, isAsc }) => `${property} ${isAsc ? 'ASC' : 'DESC'}`)
    .join(', ');
  const keyHeadCell = isTableV2 ? filterArr?.find(({ meta }) => meta?.isKey) : filterArr?.find(({ isKey }) => isKey);
  return map.length ? `ORDER BY ${orderByFields || `${(keyHeadCell || filterArr?.[0])?.id} ASC`}` : '';
};
